'use client';

import React, { useState, Suspense } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const SERVICE_LABELS: Record<string, string> = {
  'chassis-alignment': 'Car-O-Liner Chassis Alignment',
  'airbag-srs': 'Airbag & SRS Repair',
  'module-coding': 'Module Coding',
  'body-repairs': 'Body Repairs',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; next: string }> = {
  new: {
    label: 'New',
    color: '#CC1A1A',
    bg: 'rgba(204,26,26,0.12)',
    next: "We'll call you within 1 hour during business hours (Mon–Fri 8am–6pm, Sat 8am–4pm).",
  },
  contacted: {
    label: 'Contacted',
    color: '#F97316',
    bg: 'rgba(249,115,22,0.12)',
    next: "We've been in touch. If you haven't heard from us, please call 0161 400 1687.",
  },
  scheduled: {
    label: 'Scheduled',
    color: '#3B82F6',
    bg: 'rgba(59,130,246,0.12)',
    next: "Your appointment is confirmed. We'll see you soon!",
  },
  completed: {
    label: 'Completed',
    color: '#16a34a',
    bg: 'rgba(22,163,74,0.12)',
    next: 'Your repair is complete. Thank you for choosing No Limit Auto Body Specialist.',
  },
  cancelled: {
    label: 'Cancelled',
    color: '#6B7280',
    bg: 'rgba(107,114,128,0.12)',
    next: 'This booking has been cancelled. Call us on 0161 400 1687 to rebook.',
  },
};

interface BookingResult {
  reference: string | null;
  service_type: string;
  name: string;
  vehicle_reg: string;
  vehicle_make_model: string | null;
  created_at: string;
  status: string;
}

function ShimmerCard() {
  return (
    <div
      className="w-full max-w-[600px] rounded-xl p-6 mt-8"
      style={{ background: '#1a1a1a', borderLeft: '4px solid #CC1A1A', border: '1px solid rgba(204,26,26,0.2)', borderLeftWidth: '4px' }}
    >
      {[80, 60, 100, 70, 90].map((w, i) => (
        <div
          key={i}
          className="h-4 rounded mb-3 animate-pulse"
          style={{ width: `${w}%`, background: 'rgba(255,255,255,0.07)' }}
        />
      ))}
    </div>
  );
}

function TrackBookingContent() {
  const [activeTab, setActiveTab] = useState<'ref' | 'phone'>('ref');
  const [refInput, setRefInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [regInput, setRegInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<BookingResult | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setNotFound(false);
    setError(false);

    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();

      let query = supabase
        .from('bookings')
        .select('reference, service_type, name, vehicle_reg, vehicle_make_model, created_at, status');

      if (activeTab === 'ref') {
        query = query.ilike('reference', refInput.trim().toUpperCase());
      } else {
        query = query
          .eq('phone', phoneInput.trim())
          .ilike('vehicle_reg', regInput.trim().toUpperCase());
      }

      const { data, error: supaErr } = await query.limit(1).single();

      if (supaErr || !data) {
        setNotFound(true);
      } else {
        setResult(data as BookingResult);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const statusKey = result?.status?.toLowerCase() || 'new';
  const statusCfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG['new'];

  return (
    <>
      <Header />
      <main className="min-h-screen" style={{ background: '#121212' }}>
        <div className="max-w-[680px] mx-auto px-5 pt-32 pb-20">

          {/* Page heading */}
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#CC1A1A]/40 bg-[#CC1A1A]/10 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#CC1A1A] animate-pulse" />
              <span className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A]">
                Booking Tracker
              </span>
            </div>
            <h1 className="font-fraunces text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
              Track Your Booking
            </h1>
            <p className="font-dm-sans text-base" style={{ color: '#C0C0C0' }}>
              Enter your details below to check your booking status.
            </p>
          </div>

          {/* Tab switcher */}
          <div
            className="flex rounded-lg overflow-hidden mb-6"
            style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {(['ref', 'phone'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => { setActiveTab(tab); setResult(null); setNotFound(false); setError(false); }}
                className="flex-1 py-3 font-dm-sans text-sm font-semibold transition-all duration-200"
                style={{
                  background: activeTab === tab ? '#CC1A1A' : 'transparent',
                  color: activeTab === tab ? '#fff' : 'rgba(255,255,255,0.5)',
                  borderBottom: activeTab === tab ? '2px solid #CC1A1A' : '2px solid transparent',
                }}
              >
                {tab === 'ref' ? 'By Reference' : 'By Phone & Reg'}
              </button>
            ))}
          </div>

          {/* Search form */}
          <form onSubmit={handleSearch} className="space-y-4">
            {activeTab === 'ref' ? (
              <div>
                <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                  Booking Reference
                </label>
                <input
                  type="text"
                  value={refInput}
                  onChange={e => setRefInput(e.target.value.toUpperCase())}
                  placeholder="e.g. NLB-2026-A3F9K2"
                  required
                  className="w-full px-4 py-3 rounded font-dm-sans text-sm text-white placeholder-white/30 focus:outline-none"
                  style={{
                    background: '#1a1a1a',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#CC1A1A')}
                  onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phoneInput}
                    onChange={e => setPhoneInput(e.target.value)}
                    placeholder="e.g. 07700 900123"
                    required
                    className="w-full px-4 py-3 rounded font-dm-sans text-sm text-white placeholder-white/30 focus:outline-none"
                    style={{
                      background: '#1a1a1a',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                    onFocus={e => (e.currentTarget.style.borderColor = '#CC1A1A')}
                    onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
                  />
                </div>
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Vehicle Registration
                  </label>
                  <input
                    type="text"
                    value={regInput}
                    onChange={e => setRegInput(e.target.value.toUpperCase())}
                    placeholder="e.g. AB12 CDE"
                    required
                    className="w-full px-4 py-3 rounded font-dm-sans text-sm text-white placeholder-white/30 focus:outline-none"
                    style={{
                      background: '#1a1a1a',
                      border: '1px solid rgba(255,255,255,0.12)',
                    }}
                    onFocus={e => (e.currentTarget.style.borderColor = '#CC1A1A')}
                    onBlur={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded font-dm-sans text-sm font-bold uppercase tracking-widest text-white transition-all duration-200 flex items-center justify-center gap-2"
              style={{ background: loading ? '#991515' : '#CC1A1A' }}
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  Searching...
                </>
              ) : (
                'Track Booking →'
              )}
            </button>
          </form>

          {/* Loading shimmer */}
          {loading && <ShimmerCard />}

          {/* Result card */}
          {!loading && result && (
            <div
              className="w-full rounded-xl p-6 mt-8"
              style={{
                background: '#1a1a1a',
                borderLeft: '4px solid #CC1A1A',
                border: '1px solid rgba(204,26,26,0.2)',
                borderLeftWidth: '4px',
              }}
            >
              <p className="font-dm-sans text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#CC1A1A' }}>
                Booking Found
              </p>

              {/* Status badge */}
              <div className="flex items-center gap-3 mb-5">
                <span
                  className="font-dm-sans text-sm font-bold px-3 py-1 rounded-full"
                  style={{ color: statusCfg.color, background: statusCfg.bg }}
                >
                  {statusCfg.label}
                </span>
              </div>

              {/* Booking details */}
              <div className="space-y-3 mb-5">
                {result.reference && (
                  <div className="flex justify-between items-start gap-4">
                    <span className="font-dm-sans text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>Reference</span>
                    <span className="font-dm-sans text-sm font-bold text-white text-right">{result.reference}</span>
                  </div>
                )}
                <div className="flex justify-between items-start gap-4">
                  <span className="font-dm-sans text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>Service</span>
                  <span className="font-dm-sans text-sm text-white text-right">{SERVICE_LABELS[result.service_type] || result.service_type}</span>
                </div>
                <div className="flex justify-between items-start gap-4">
                  <span className="font-dm-sans text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>Name</span>
                  <span className="font-dm-sans text-sm text-white text-right">{result.name}</span>
                </div>
                <div className="flex justify-between items-start gap-4">
                  <span className="font-dm-sans text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>Vehicle</span>
                  <span className="font-dm-sans text-sm text-white text-right">
                    {result.vehicle_make_model ? `${result.vehicle_make_model} – ` : ''}{result.vehicle_reg}
                  </span>
                </div>
                <div className="flex justify-between items-start gap-4">
                  <span className="font-dm-sans text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.4)' }}>Submitted</span>
                  <span className="font-dm-sans text-sm text-white text-right">{formatDate(result.created_at)}</span>
                </div>
              </div>

              {/* What happens next */}
              <div
                className="rounded-lg p-4 mb-5"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <p className="font-dm-sans text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  What happens next
                </p>
                <p className="font-dm-sans text-sm leading-relaxed" style={{ color: '#C0C0C0' }}>
                  {statusCfg.next}
                </p>
              </div>

              {/* Need help */}
              <div className="pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <p className="font-dm-sans text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Need help?
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:01614001687"
                    className="flex items-center gap-2 font-dm-sans text-sm font-semibold transition-colors duration-200"
                    style={{ color: '#CC1A1A' }}
                  >
                    📞 0161 400 1687
                  </a>
                  <a
                    href="mailto:contactus@nolimitauto.co.uk"
                    className="flex items-center gap-2 font-dm-sans text-sm font-semibold transition-colors duration-200"
                    style={{ color: '#CC1A1A' }}
                  >
                    ✉️ contactus@nolimitauto.co.uk
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Not found state */}
          {!loading && notFound && (
            <div
              className="w-full rounded-xl p-6 mt-8 text-center"
              style={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              <p className="font-dm-sans text-base text-white mb-2 font-semibold">No booking found</p>
              <p className="font-dm-sans text-sm" style={{ color: '#C0C0C0' }}>
                No booking found with those details. Please check your reference or call us on{' '}
                <a href="tel:01614001687" style={{ color: '#CC1A1A' }} className="font-semibold">
                  0161 400 1687
                </a>
                .
              </p>
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div
              className="w-full rounded-xl p-6 mt-8 text-center"
              style={{ background: '#1a1a1a', border: '1px solid rgba(204,26,26,0.25)' }}
            >
              <p className="font-dm-sans text-sm" style={{ color: '#C0C0C0' }}>
                Unable to check status right now. Please call{' '}
                <a href="tel:01614001687" style={{ color: '#CC1A1A' }} className="font-semibold">
                  0161 400 1687
                </a>
                .
              </p>
            </div>
          )}

          {/* Back link */}
          <div className="mt-12 text-center">
            <Link
              href="/"
              className="font-dm-sans text-sm transition-colors duration-200"
              style={{ color: 'rgba(255,255,255,0.35)' }}
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export default function TrackBookingPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center" style={{ background: '#121212' }}>
          <div className="w-8 h-8 rounded-full border-2 border-[#CC1A1A] border-t-transparent animate-spin" />
        </div>
      }
    >
      <TrackBookingContent />
    </Suspense>
  );
}
